import { z } from 'zod';
import {
  AccountProvider,
  AccountType,
  CategoryKind,
  ConnectionStatus,
  ExpenseType,
  InvestmentEventType,
  RecurringCadence,
  RecurringKind,
  RecurringStatus,
  RuleScope,
  SettlementStatus,
  SplitMethod,
  TransactionKind,
  TransactionSource,
  TransactionStatus,
  Visibility,
  type EntityType,
} from './enums.ts';
import {
  Color,
  CurrencyCode,
  IsoDate,
  MinorAmount,
  Name,
  NonNegativeInteger,
  OptionalText,
  Timestamp,
  Uuid,
} from './primitives.ts';

// ---------------------------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------------------------

/** Server-controlled columns present on every synced aggregate. */
const Meta = {
  id: Uuid,
  householdId: Uuid,
  version: z.number().int().min(1),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  deletedAt: Timestamp.nullable(),
};

export const Participant = z.strictObject({
  personId: Uuid,
  /** Basis points (PERCENTAGES), weight (SHARES) or minor units (EXACT); null otherwise. */
  value: NonNegativeInteger.nullable(),
});
export type Participant = z.infer<typeof Participant>;

const INTEGER = /^-?\d+$/;
/** Parses integer text; null for anything else (refinements also run on already-invalid input). */
const big = (text: string | null | undefined): bigint | null =>
  typeof text === 'string' && INTEGER.test(text) ? BigInt(text) : null;

/** Checks one allocation (method + participants) against the amount it splits. */
function checkAllocation(
  method: z.infer<typeof SplitMethod> | null,
  participants: readonly Participant[],
  amountMinor: string,
  context: z.RefinementCtx,
  path: (string | number)[],
) {
  const ids = participants.map((p) => p.personId);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: 'custom', message: 'Duplicate participant', path });
  }
  if (method === null) return;
  if (participants.length === 0) {
    context.addIssue({ code: 'custom', message: 'A split method needs participants', path });
    return;
  }
  const values = participants.map((p) => big(p.value));
  if (method === 'PERCENTAGES' && (values.some((v) => v === null) || values.reduce<bigint>((a, v) => a + (v ?? 0n), 0n) !== 10_000n)) {
    context.addIssue({ code: 'custom', message: 'Percentages must add up to 100%', path });
  }
  if (method === 'SHARES' && (values.some((v) => v === null) || values.every((v) => v === 0n))) {
    context.addIssue({ code: 'custom', message: 'Shares must be positive', path });
  }
  if (method === 'EXACT') {
    const total = big(amountMinor);
    if (total === null) return;
    const magnitude = total < 0n ? -total : total;
    if (values.some((v) => v === null) || values.reduce<bigint>((a, v) => a + (v ?? 0n), 0n) !== magnitude) {
      context.addIssue({ code: 'custom', message: 'Exact amounts must add up to the total', path });
    }
  }
}

const EffectiveRange = {
  effectiveFrom: IsoDate,
  effectiveTo: IsoDate.nullable(),
};

function checkRanges(
  ranges: readonly { effectiveFrom: string; effectiveTo: string | null }[],
  context: z.RefinementCtx,
  path: (string | number)[],
) {
  const sorted = [...ranges].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
  sorted.forEach((range, index) => {
    if (range.effectiveTo !== null && range.effectiveTo <= range.effectiveFrom) {
      context.addIssue({ code: 'custom', message: 'effectiveTo must be after effectiveFrom', path });
    }
    const next = sorted[index + 1];
    if (next && (range.effectiveTo === null || range.effectiveTo > next.effectiveFrom)) {
      context.addIssue({ code: 'custom', message: 'Effective periods overlap', path });
    }
  });
}

// ---------------------------------------------------------------------------------------------
// Household and people
// ---------------------------------------------------------------------------------------------

export const HouseholdInput = z.strictObject({
  name: Name,
  baseCurrency: CurrencyCode,
  /** The person who represents the owner (OwnerPersonLink). */
  ownerPersonId: Uuid.nullable(),
  /**
   * Counting starts on this day (D-040): earlier transactions stay stored but count nowhere, are
   * not sent to devices and are not imported. Null: everything counts.
   */
  countFrom: IsoDate.nullable().default(null),
  /**
   * The dashboard's cards where the owner put them, the same on every device: the board of a wide
   * screen and the order of a narrow one, as the app stores them (JSON). Null: the default.
   */
  dashboardBoard: z.string().max(4000).nullable().default(null),
  dashboardList: z.string().max(2000).nullable().default(null),
});
export const Household = z.strictObject({ ...Meta, ...HouseholdInput.shape });

export const PersonInput = z.strictObject({
  displayName: Name,
  color: Color,
  avatar: z.string().max(16).nullable(),
  sortOrder: z.number().int().min(0).max(10_000),
  archivedAt: Timestamp.nullable(),
  /** The account the person's pay arrives on; null = found from the accounts they alone own. */
  incomeAccountId: Uuid.nullable().default(null),
});
export const Person = z.strictObject({ ...Meta, ...PersonInput.shape });

// ---------------------------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------------------------

export const FundingVersion = z
  .strictObject({
    id: Uuid,
    ...EffectiveRange,
    shares: z
      .array(z.strictObject({ personId: Uuid, basisPoints: z.number().int().min(0).max(10_000) }))
      .min(1)
      .max(20),
  })
  .superRefine((version, context) => {
    const total = version.shares.reduce((acc, s) => acc + s.basisPoints, 0);
    if (total !== 10_000) context.addIssue({ code: 'custom', message: 'Funding shares must add up to 100%' });
  });

const accountInputShape = {
  name: Name,
  institutionName: OptionalText(120),
  type: AccountType,
  currency: CurrencyCode,
  openingBalanceMinor: MinorAmount,
  openingBalanceDate: IsoDate,
  visibility: Visibility,
  defaultExpenseType: ExpenseType.nullable(),
  isSavingsAsset: z.boolean(),
  includeInNetWorth: z.boolean(),
  archivedAt: Timestamp.nullable(),
  sortOrder: z.number().int().min(0).max(10_000),
  color: Color,
  owners: z.array(Uuid).max(20),
  fundingVersions: z.array(FundingVersion).max(50),
  /** Loan terms (LOAN, MORTGAGE, CREDIT_CARD, LIABILITY); null elsewhere or when unknown. */
  interestRateBasisPoints: z.number().int().min(0).max(100_000).nullable(),
  paymentMinor: MinorAmount.refine((v) => !v.startsWith('-'), 'Payment must not be negative').nullable(),
  paymentDay: z.number().int().min(1).max(31).nullable(),
  endsOn: IsoDate.nullable(),
  originalPrincipalMinor: MinorAmount.refine((v) => !v.startsWith('-'), 'Principal must not be negative').nullable(),
  /** The agreement's schedule (D-029): regular monthly payments from the first one, then an optional last one. */
  paymentCount: z.number().int().min(1).max(600).nullable().default(null),
  finalPaymentMinor: MinorAmount.refine((v) => !v.startsWith('-'), 'Payment must not be negative').nullable().default(null),
  firstPaymentDate: IsoDate.nullable().default(null),
  /** Personal loan, or car finance: PCP (the last payment is optional) or HP. */
  financeType: z.enum(['PERSONAL', 'PCP', 'HP']).nullable().default(null),
  /** What the loan paid for, kept as an asset account (the car on car finance, D-030). */
  assetAccountId: Uuid.nullable().default(null),
  /** UK tax wrapper of a savings or investment account; drives the ISA allowance. */
  taxWrapper: z.enum(['ISA', 'LISA', 'SIPP', 'PENSION']).nullable().default(null),
};

const refineAccount = (account: { owners: string[]; fundingVersions: { effectiveFrom: string; effectiveTo: string | null }[] }, context: z.RefinementCtx) => {
  if (new Set(account.owners).size !== account.owners.length) {
    context.addIssue({ code: 'custom', message: 'Duplicate owner', path: ['owners'] });
  }
  checkRanges(account.fundingVersions, context, ['fundingVersions']);
};

export const AccountInput = z.strictObject(accountInputShape).superRefine(refineAccount);
export const Account = z
  .strictObject({
    ...Meta,
    ...accountInputShape,
    balanceMinor: MinorAmount,
    availableBalanceMinor: MinorAmount.nullable(),
    balanceAsOf: Timestamp.nullable(),
    provider: AccountProvider,
    bankConnectionId: Uuid.nullable(),
    investmentConnectionId: Uuid.nullable(),
    lastSyncedAt: Timestamp.nullable(),
    consentExpiresAt: Timestamp.nullable(),
    closingBalanceMinor: MinorAmount.nullable(),
  })
  .superRefine(refineAccount);

// ---------------------------------------------------------------------------------------------
// Categories and tags
// ---------------------------------------------------------------------------------------------

export const CategoryGroupInput = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/).nullable(),
  name: Name.nullable(),
  /** Shown for the group as a whole (the cash-flow card, a group's budget). */
  icon: z.string().max(40).nullable().default(null),
  color: Color.default(null),
  sortOrder: z.number().int().min(0).max(10_000),
  archivedAt: Timestamp.nullable(),
});
export const CategoryGroup = z.strictObject({ ...Meta, ...CategoryGroupInput.shape });

export const CategoryInput = z
  .strictObject({
    /** Stable key of a system category ("groceries"); null for the user's own. */
    key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/).nullable(),
    /** User-given name; for system categories it overrides the translation. */
    name: Name.nullable(),
    icon: z.string().max(40).nullable(),
    color: Color,
    groupId: Uuid.nullable(),
    kind: CategoryKind,
    sortOrder: z.number().int().min(0).max(10_000),
    archivedAt: Timestamp.nullable(),
    /** New transactions in it come in shared (or personal) — D-037; the history keeps its own. */
    defaultExpenseType: z.enum(['SHARED', 'PERSONAL']).nullable().default(null),
  })
  .refine((c) => c.key !== null || c.name !== null, { message: 'A category needs a key or a name' });
export const Category = z.strictObject({ ...Meta, ...CategoryInput.shape });

export const TagInput = z.strictObject({
  name: Name,
  color: Color,
  archivedAt: Timestamp.nullable(),
});
export const Tag = z.strictObject({ ...Meta, ...TagInput.shape });

// ---------------------------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------------------------

export const TransactionSplit = z.strictObject({
  id: Uuid,
  amountMinor: MinorAmount,
  categoryId: Uuid.nullable(),
  expenseType: ExpenseType.nullable(),
  splitMethod: SplitMethod.nullable(),
  participants: z.array(Participant).max(20),
  notes: OptionalText(500),
  sortOrder: z.number().int().min(0).max(1000),
});

const transactionInputShape = {
  accountId: Uuid,
  bookingDate: IsoDate,
  valueDate: IsoDate.nullable(),
  amountMinor: MinorAmount,
  currency: CurrencyCode,
  merchantName: OptionalText(120),
  description: OptionalText(500),
  categoryId: Uuid.nullable(),
  kind: TransactionKind,
  status: TransactionStatus,
  source: TransactionSource,
  expenseType: ExpenseType.nullable(),
  splitMethod: SplitMethod.nullable(),
  participants: z.array(Participant).max(20),
  paidByPersonId: Uuid.nullable(),
  counterpartAccountId: Uuid.nullable(),
  refundOfTransactionId: Uuid.nullable(),
  splits: z.array(TransactionSplit).max(50),
  tagIds: z.array(Uuid).max(30),
  notes: OptionalText(2000),
  needsReview: z.boolean(),
};

type TransactionLike = {
  amountMinor: string;
  kind: z.infer<typeof TransactionKind>;
  splitMethod: z.infer<typeof SplitMethod> | null;
  participants: Participant[];
  splits: z.infer<typeof TransactionSplit>[];
  counterpartAccountId: string | null;
  accountId: string;
  tagIds: string[];
};

/** Business rules that must hold for every stored transaction (checked again after merges). */
export function refineTransaction(transaction: TransactionLike, context: z.RefinementCtx) {
  const parsed = big(transaction.amountMinor);
  if (parsed === null) return;
  const amount = parsed;
  const sign = amount < 0n ? -1n : amount > 0n ? 1n : 0n;
  const expectNegative = transaction.kind === 'EXPENSE';
  const expectPositive = transaction.kind === 'INCOME' || transaction.kind === 'REFUND';
  if (expectNegative && sign >= 0n) {
    context.addIssue({ code: 'custom', message: 'An expense must be negative', path: ['amountMinor'] });
  }
  if (expectPositive && sign <= 0n) {
    context.addIssue({ code: 'custom', message: 'Income and refunds must be positive', path: ['amountMinor'] });
  }
  if (transaction.counterpartAccountId !== null && transaction.counterpartAccountId === transaction.accountId) {
    context.addIssue({ code: 'custom', message: 'A transfer needs a different account', path: ['counterpartAccountId'] });
  }
  if (new Set(transaction.tagIds).size !== transaction.tagIds.length) {
    context.addIssue({ code: 'custom', message: 'Duplicate tag', path: ['tagIds'] });
  }
  checkAllocation(transaction.splitMethod, transaction.participants, transaction.amountMinor, context, ['participants']);
  if (transaction.splits.length > 0) {
    let total = 0n;
    transaction.splits.forEach((split, index) => {
      const splitAmount = big(split.amountMinor) ?? 0n;
      total += splitAmount;
      if (splitAmount !== 0n && sign !== 0n && (splitAmount < 0n ? -1n : 1n) !== sign) {
        context.addIssue({ code: 'custom', message: 'A split must have the sign of the transaction', path: ['splits', index] });
      }
      checkAllocation(split.splitMethod, split.participants, split.amountMinor, context, ['splits', index, 'participants']);
    });
    if (total !== amount) {
      context.addIssue({ code: 'custom', message: 'Splits must add up to the transaction amount', path: ['splits'] });
    }
    const ids = transaction.splits.map((s) => s.id);
    if (new Set(ids).size !== ids.length) {
      context.addIssue({ code: 'custom', message: 'Duplicate split id', path: ['splits'] });
    }
  }
}

export const TransactionInput = z.strictObject(transactionInputShape).superRefine(refineTransaction);

export const transactionServerShape = {
  externalId: z.string().max(200).nullable(),
  provider: AccountProvider.nullable(),
  descriptionRaw: OptionalText(500),
  baseAmountMinor: MinorAmount.nullable(),
  fxRate: z.string().regex(/^\d+(\.\d+)?$/).nullable(),
  fxRateSource: z.string().max(40).nullable(),
  fxRateDate: IsoDate.nullable(),
  paymentType: z.string().max(40).nullable(),
  isRecurringCandidate: z.boolean(),
  recurringPatternId: Uuid.nullable(),
  reviewReasons: z.array(z.string().max(60)).max(20),
  overriddenFields: z.array(z.string().max(60)).max(40),
  importedAt: Timestamp.nullable(),
};

export const Transaction = z
  .strictObject({ ...Meta, ...transactionInputShape, ...transactionServerShape })
  .superRefine(refineTransaction);

// ---------------------------------------------------------------------------------------------
// Settlement rules, incomes, settlements
// ---------------------------------------------------------------------------------------------

export const IncomePeriodInput = z
  .strictObject({
    personId: Uuid,
    amountMinor: MinorAmount.refine((v) => !v.startsWith('-'), 'Income must not be negative'),
    currency: CurrencyCode,
    ...EffectiveRange,
    source: z.enum(['MANUAL', 'DERIVED']),
  })
  .refine((p) => p.effectiveTo === null || p.effectiveTo > p.effectiveFrom, {
    message: 'effectiveTo must be after effectiveFrom',
  });
export const IncomePeriod = z.strictObject({ ...Meta, ...IncomePeriodInput.shape });

export const RuleVersion = z.strictObject({
  id: Uuid,
  ...EffectiveRange,
  method: SplitMethod,
  participants: z.array(Participant).min(1).max(20),
});

const settlementRuleShape = {
  scope: RuleScope,
  scopeId: Uuid.nullable(),
  versions: z.array(RuleVersion).min(1).max(100),
};

const refineRule = (
  rule: { scope: string; scopeId: string | null; versions: z.infer<typeof RuleVersion>[] },
  context: z.RefinementCtx,
) => {
  if ((rule.scope === 'HOUSEHOLD') !== (rule.scopeId === null)) {
    context.addIssue({ code: 'custom', message: 'Only the household rule has no scope id', path: ['scopeId'] });
  }
  checkRanges(rule.versions, context, ['versions']);
  rule.versions.forEach((version, index) => {
    if (version.method === 'EXACT') {
      context.addIssue({ code: 'custom', message: 'Rules cannot use exact amounts', path: ['versions', index] });
    } else {
      checkAllocation(version.method, version.participants, '0', context, ['versions', index]);
    }
  });
};

export const SettlementRuleInput = z.strictObject(settlementRuleShape).superRefine(refineRule);
export const SettlementRule = z.strictObject({ ...Meta, ...settlementRuleShape }).superRefine(refineRule);

const settlementShape = {
  periodStart: IsoDate.nullable(),
  periodEnd: IsoDate,
  settledOn: IsoDate,
  currency: CurrencyCode,
  status: SettlementStatus,
  note: OptionalText(500),
  ruleSnapshot: z.unknown().nullable(),
  transfers: z
    .array(
      z.strictObject({
        id: Uuid,
        fromPersonId: Uuid,
        toPersonId: Uuid,
        amountMinor: MinorAmount.refine((v) => BigInt(v) > 0n, 'Transfer must be positive'),
        paymentTransactionId: Uuid.nullable(),
      }),
    )
    .max(50),
  items: z.array(z.strictObject({ transactionId: Uuid, splitId: Uuid.nullable() })).max(20_000),
};
export const SettlementInput = z.strictObject(settlementShape);
export const Settlement = z.strictObject({ ...Meta, ...settlementShape });

// ---------------------------------------------------------------------------------------------
// Integrations (written only by the backend; secrets never appear here)
// ---------------------------------------------------------------------------------------------

export const InvestmentConnection = z.strictObject({
  ...Meta,
  provider: z.enum(['TRADING212', 'FAKE_INVEST']),
  environment: z.enum(['LIVE', 'DEMO']),
  label: Name,
  accountKindLabel: z.enum(['INVEST', 'STOCKS_ISA']),
  accessMode: z.literal('READ_ONLY'),
  status: ConnectionStatus,
  primaryCurrency: CurrencyCode.nullable(),
  accountId: Uuid.nullable(),
  /** Last characters of the key id, to recognise it — never the key itself. */
  keyHint: z.string().max(8).nullable(),
  lastSyncedAt: Timestamp.nullable(),
  lastSuccessAt: Timestamp.nullable(),
  lastErrorCode: z.string().max(60).nullable(),
  historyComplete: z.boolean(),
});

export const InvestmentPosition = z.strictObject({
  ticker: z.string().max(40),
  name: z.string().max(200),
  isin: z.string().max(20).nullable(),
  /** Decimal quantities as text (fractional shares). */
  quantity: z.string().regex(/^\d+(\.\d+)?$/),
  /** Part of the quantity held through a pie (Trading 212). */
  quantityInPies: z.string().regex(/^\d+(\.\d+)?$/),
  averagePrice: z.string().regex(/^\d+(\.\d+)?$/),
  currentPrice: z.string().regex(/^\d+(\.\d+)?$/),
  priceCurrency: z.string().regex(/^[A-Z]{3}$/),
  valueMinor: MinorAmount,
  costMinor: MinorAmount,
  pnlMinor: MinorAmount,
  fxImpactMinor: MinorAmount.nullable(),
});

export const InvestmentPortfolio = z.strictObject({
  ...Meta,
  accountId: Uuid,
  connectionId: Uuid,
  currency: CurrencyCode,
  totalValueMinor: MinorAmount,
  cashMinor: MinorAmount,
  investedMinor: MinorAmount,
  unrealizedPnlMinor: MinorAmount,
  realizedPnlMinor: MinorAmount.nullable(),
  asOf: Timestamp,
  positions: z.array(InvestmentPosition).max(1000),
  /** Daily closing values for charts (most recent ~400 days). */
  valueHistory: z.array(z.strictObject({ date: IsoDate, valueMinor: MinorAmount })).max(800),
});

export const InvestmentEvent = z.strictObject({
  ...Meta,
  accountId: Uuid,
  type: InvestmentEventType,
  externalId: z.string().max(200),
  occurredAt: Timestamp,
  bookingDate: IsoDate,
  amountMinor: MinorAmount,
  currency: CurrencyCode,
  ticker: z.string().max(40).nullable(),
  quantity: z.string().regex(/^-?\d+(\.\d+)?$/).nullable(),
  matchedTransactionId: Uuid.nullable(),
});

/** An account the bank reported, and what the owner decided to do with it. */
export const BankConnectionAccount = z.strictObject({
  externalId: z.string().max(200),
  name: z.string().max(120),
  type: z.enum(['CURRENT', 'SAVINGS', 'CREDIT_CARD']),
  currency: CurrencyCode,
  /** Last digits only ("••6411"). */
  mask: z.string().max(12).nullable(),
  balanceMinor: MinorAmount,
  /** PENDING until the owner adds it (LINKED, to `accountId`) or skips it (IGNORED). */
  status: z.enum(['PENDING', 'LINKED', 'IGNORED']),
  accountId: Uuid.nullable(),
  /** Linked to an account with manual history: bank transactions are imported from this day. */
  importFrom: IsoDate.nullable(),
  /** The bank itself when one provider reaches several ("Lloyds Personal" through Lunch Flow). */
  institutionName: z.string().max(120).nullable().optional(),
  /** RECONNECT: the owner must confirm access at the bank again; ERROR: the provider reports trouble. */
  access: z.enum(['OK', 'RECONNECT', 'ERROR']).optional(),
  /** False once the provider stops listing the account; its history stays. */
  visible: z.boolean().optional(),
  /**
   * True once the account's whole history (from `importFrom`) has been downloaded. Until then a read
   * asks for all of it, not only the days since the connection's last success — an account added to
   * a connection that has been reading for a while would otherwise start at that day.
   */
  historyRead: z.boolean().optional(),
  /**
   * The day the provider's access to the account began or was last renewed: the bank's consent runs
   * from it when the provider does not say when it ends (Lunch Flow, 90 days).
   */
  accessSince: IsoDate.optional(),
});

export const BankConnection = z.strictObject({
  ...Meta,
  provider: z.enum(['FAKE_BANK', 'TRUELAYER', 'LUNCHFLOW']),
  institutionId: z.string().max(120),
  institutionName: Name,
  country: z.string().regex(/^[A-Z]{2}$/),
  status: ConnectionStatus,
  consentCreatedAt: Timestamp.nullable(),
  consentExpiresAt: Timestamp.nullable(),
  /** The owner must confirm access at the bank again (consent ended or was withdrawn). */
  reauthorizationRequired: z.boolean(),
  lastSyncAttemptAt: Timestamp.nullable(),
  lastSyncedAt: Timestamp.nullable(),
  lastSuccessAt: Timestamp.nullable(),
  /** Earliest next unattended read (banks allow only a few a day without the owner). */
  nextRefreshAt: Timestamp.nullable(),
  lastErrorCode: z.string().max(60).nullable(),
  accounts: z.array(BankConnectionAccount).max(100),
  lastRun: z
    .strictObject({
      at: Timestamp,
      fetched: z.number().int().min(0),
      inserted: z.number().int().min(0),
      updated: z.number().int().min(0),
      paired: z.number().int().min(0),
      duplicates: z.number().int().min(0),
    })
    .nullable(),
});

// ---------------------------------------------------------------------------------------------
// Recurring patterns: the owner's decision about a detected pattern (finance-core detects)
// ---------------------------------------------------------------------------------------------

export const RecurringPatternInput = z.strictObject({
  signature: z.string().min(1).max(400),
  merchantKey: z.string().min(1).max(200),
  name: Name,
  accountId: Uuid,
  currency: CurrencyCode,
  direction: z.enum(['IN', 'OUT']),
  cadence: RecurringCadence,
  kind: RecurringKind,
  expectedAmountMinor: MinorAmount.refine((v) => !v.startsWith('-'), 'Amount must not be negative'),
  nextExpectedDate: IsoDate.nullable(),
  confidence: z.number().int().min(0).max(10_000),
  status: RecurringStatus,
  /** The loan or card account this payment pays off. */
  linkedAccountId: Uuid.nullable(),
  categoryId: Uuid.nullable(),
  personId: Uuid.nullable(),
  /** The owner set the kind (it then outlasts the detection). */
  kindChosen: z.boolean().default(false),
});
export const RecurringPattern = z.strictObject({ ...Meta, ...RecurringPatternInput.shape });

// ---------------------------------------------------------------------------------------------
// Budgets (D-032): a monthly limit for an expense category, or for all spending
// ---------------------------------------------------------------------------------------------

export const BudgetInput = z.strictObject({
  /** Null (with no group either): all spending. */
  categoryId: Uuid.nullable(),
  /** A whole group of categories instead of one (D-035); never both. */
  categoryGroupId: Uuid.nullable().default(null),
  amountMinor: MinorAmount.refine((v) => !v.startsWith('-') && v !== '0', 'A budget must be above zero'),
  currency: CurrencyCode,
  period: z.enum(['MONTHLY']).default('MONTHLY'),
  /** What was left (or overspent) moves into the next month. */
  rollover: z.boolean().default(false),
  /** The first month it applies to. */
  effectiveFrom: IsoDate,
  /**
   * The months of the year it applies to, one bit each (January 1, February 2 … December 2048):
   * once a year (car insurance) or some months only (council tax, ten of twelve). Null: every
   * month (D-048).
   */
  monthMask: z.number().int().min(1).max(4095).nullable().default(null),
  /**
   * Once a year: a day of the payment in its month. The owner chose month precision (30.09.2026),
   * so the app leaves it empty; kept for the budgets and the column that already have it.
   */
  dueDay: z.number().int().min(1).max(31).nullable().default(null),
  archivedAt: Timestamp.nullable(),
});
export const Budget = z.strictObject({ ...Meta, ...BudgetInput.shape });

/** One dedicated account per active goal, or a manually tracked savings pot. */
export const SavingsGoalInput = z.strictObject({
  name: Name,
  targetMinor: NonNegativeInteger.refine((v) => (big(v) ?? 0n) > 0n, 'Target must be above zero'),
  currency: CurrencyCode,
  accountId: Uuid.nullable(),
  savedMinor: NonNegativeInteger,
  monthlyMinor: NonNegativeInteger,
  targetDate: IsoDate.nullable(),
  archivedAt: Timestamp.nullable(),
});
export const SavingsGoal = z.strictObject({ ...Meta, ...SavingsGoalInput.shape });

// ---------------------------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------------------------

export const ENTITY_SCHEMAS = {
  household: Household,
  person: Person,
  account: Account,
  categoryGroup: CategoryGroup,
  category: Category,
  tag: Tag,
  transaction: Transaction,
  incomePeriod: IncomePeriod,
  settlementRule: SettlementRule,
  settlement: Settlement,
  recurringPattern: RecurringPattern,
  investmentConnection: InvestmentConnection,
  investmentPortfolio: InvestmentPortfolio,
  investmentEvent: InvestmentEvent,
  bankConnection: BankConnection,
  budget: Budget,
  savingsGoal: SavingsGoal,
} satisfies Record<EntityType, z.ZodType>;

export const INPUT_SCHEMAS = {
  household: HouseholdInput,
  person: PersonInput,
  account: AccountInput,
  categoryGroup: CategoryGroupInput,
  category: CategoryInput,
  tag: TagInput,
  transaction: TransactionInput,
  incomePeriod: IncomePeriodInput,
  settlementRule: SettlementRuleInput,
  settlement: SettlementInput,
  recurringPattern: RecurringPatternInput,
  budget: BudgetInput,
  savingsGoal: SavingsGoalInput,
} as const;

export type WritableEntityType = keyof typeof INPUT_SCHEMAS;

export type Household = z.infer<typeof Household>;
export type Person = z.infer<typeof Person>;
export type Account = z.infer<typeof Account>;
export type FundingVersion = z.infer<typeof FundingVersion>;
export type CategoryGroup = z.infer<typeof CategoryGroup>;
export type Category = z.infer<typeof Category>;
export type Tag = z.infer<typeof Tag>;
export type Transaction = z.infer<typeof Transaction>;
export type TransactionSplit = z.infer<typeof TransactionSplit>;
export type IncomePeriod = z.infer<typeof IncomePeriod>;
export type SettlementRule = z.infer<typeof SettlementRule>;
export type RuleVersion = z.infer<typeof RuleVersion>;
export type Settlement = z.infer<typeof Settlement>;
export type InvestmentConnection = z.infer<typeof InvestmentConnection>;
export type InvestmentPortfolio = z.infer<typeof InvestmentPortfolio>;
export type InvestmentPosition = z.infer<typeof InvestmentPosition>;
export type InvestmentEvent = z.infer<typeof InvestmentEvent>;
export type BankConnection = z.infer<typeof BankConnection>;
export type BankConnectionAccount = z.infer<typeof BankConnectionAccount>;
export type RecurringPattern = z.infer<typeof RecurringPattern>;
export type Budget = z.infer<typeof Budget>;
export type SavingsGoal = z.infer<typeof SavingsGoal>;

/** Fields that must change together: a concurrent edit to any of them is a conflict (SYNC_PROTOCOL §5). */
export const FINANCIAL_FIELD_GROUPS: Partial<Record<EntityType, readonly (readonly string[])[]>> = {
  transaction: [
    [
      'amountMinor',
      'currency',
      'paidByPersonId',
      'participants',
      'splitMethod',
      'splits',
      'expenseType',
      'kind',
      'accountId',
      'counterpartAccountId',
      'refundOfTransactionId',
    ],
  ],
  account: [
    ['owners', 'fundingVersions', 'visibility', 'currency', 'openingBalanceMinor', 'openingBalanceDate'],
    ['interestRateBasisPoints', 'paymentMinor', 'paymentDay', 'endsOn', 'originalPrincipalMinor', 'paymentCount', 'finalPaymentMinor', 'firstPaymentDate', 'financeType', 'assetAccountId'],
  ],
  incomePeriod: [['personId', 'amountMinor', 'currency', 'effectiveFrom', 'effectiveTo']],
  settlementRule: [['scope', 'scopeId', 'versions']],
  settlement: [
    ['periodStart', 'periodEnd', 'settledOn', 'currency', 'status', 'note', 'ruleSnapshot', 'transfers', 'items'],
  ],
  budget: [['amountMinor', 'currency', 'rollover', 'effectiveFrom']],
  savingsGoal: [['targetMinor', 'currency', 'accountId', 'savedMinor', 'monthlyMinor', 'targetDate', 'archivedAt']],
};

/** Fields a client may not change after creation. */
export const IMMUTABLE_FIELDS: Partial<Record<EntityType, readonly string[]>> = {
  account: ['currency'],
  settlementRule: ['scope', 'scopeId'],
  incomePeriod: ['personId'],
  recurringPattern: ['signature', 'accountId', 'currency', 'direction'],
  budget: ['categoryId', 'categoryGroupId', 'currency'],
  savingsGoal: ['currency'],
};
